import React from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { LifeBuoy, ClipboardList, Layers, ArrowRight } from "lucide-react";
import { usePortalAuth } from "./portalAuth";

export default function PortalDashboard() {
  const { user } = usePortalAuth();
  const [summary, setSummary] = React.useState(null);
  const [recentTickets, setRecentTickets] = React.useState([]);
  const [activeProjects, setActiveProjects] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      const [s, t, p] = await Promise.all([
        api.get("/portal/me/summary"),
        api.get("/portal/me/tickets"),
        api.get("/portal/me/projects"),
      ]);
      setSummary(s.data);
      setRecentTickets(t.data.slice(0, 5));
      setActiveProjects(p.data.filter((x) => x.status !== "Completed").slice(0, 5));
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Welcome back</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">{user.name.split(" ")[0]}</h1>
        <p className="text-slate-500 text-sm mt-1">Everything Xentra is doing for {user.account.name}.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="portal-summary-tiles">
        <Tile icon={LifeBuoy} label="Open tickets"
          value={summary?.open_tickets ?? "—"} sub={`${summary?.total_tickets ?? 0} total`} accent="blue" />
        <Tile icon={ClipboardList} label="Active projects"
          value={summary?.open_projects ?? "—"} sub={`${summary?.total_projects ?? 0} total`} accent="emerald" />
        <Tile icon={Layers} label="Live products"
          value={summary?.live_products ?? "—"} sub="Active in your stack" accent="amber" />
      </div>

      <Card className="border-slate-200">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase font-mono text-slate-500 tracking-wider">Recent support tickets</div>
            <h2 className="font-heading text-lg font-bold text-slate-900 mt-0.5">What we're working on</h2>
          </div>
          <Link to="/portal/tickets" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <CardContent className="p-0">
          {recentTickets.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No tickets yet — that's a good sign!</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Priority</th>
                  <th className="px-4 py-2">Opened</th>
                </tr>
              </thead>
              <tbody>
                {recentTickets.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100" data-testid={`portal-recent-ticket-${t.id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{t.subject || t.title}</td>
                    <td className="px-4 py-2"><StatusBadge value={t.status} /></td>
                    <td className="px-4 py-2 text-slate-600">{t.priority}</td>
                    <td className="px-4 py-2 text-slate-500 font-mono text-[12px]">{fmtDate(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase font-mono text-slate-500 tracking-wider">Active projects</div>
            <h2 className="font-heading text-lg font-bold text-slate-900 mt-0.5">In flight</h2>
          </div>
          <Link to="/portal/projects" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <CardContent className="p-5">
          {activeProjects.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-6">No active projects.</div>
          ) : (
            <div className="space-y-3">
              {activeProjects.map((p) => {
                const total = (p.steps || []).length;
                const done = (p.steps || []).filter((s) => s.done).length;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={p.id} className="border border-slate-200 rounded-md p-3" data-testid={`portal-active-project-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-slate-900">{p.name}</div>
                      <div className="text-xs font-mono text-slate-500">{done}/{total} steps</div>
                    </div>
                    <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const TONES = {
  blue: "text-blue-700 bg-blue-50 border-blue-100",
  emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
  amber: "text-amber-700 bg-amber-50 border-amber-100",
};

function Tile({ icon: Icon, label, value, sub, accent = "blue" }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase font-mono text-slate-500 tracking-wider">{label}</div>
          <div className={`h-7 w-7 rounded-md border flex items-center justify-center ${TONES[accent]}`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
        </div>
        <div className="mt-3 font-heading text-3xl font-bold text-slate-900">{value}</div>
        <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ value }) {
  const palette = {
    "New": "bg-blue-50 text-blue-700 border-blue-100",
    "Open": "bg-amber-50 text-amber-700 border-amber-100",
    "Waiting on Customer": "bg-orange-50 text-orange-700 border-orange-100",
    "Waiting on Internal": "bg-violet-50 text-violet-700 border-violet-100",
    "Resolved": "bg-emerald-50 text-emerald-700 border-emerald-100",
    "Closed": "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase font-mono tracking-wider rounded border ${palette[value] || palette.Open}`}>
      {value}
    </span>
  );
}
